import { ca } from '@cian/analytics';
import { IBreadcrumb, IOffer, offerHelper, EOfferExtensionType } from '../offer';
import { IFiltersState, IResultsState, IUserGADataLayerData, TUser } from '../common/state/app_state';
import { IJsonQuery } from '../json_query';
import { getOfferExtra } from '../tracking/getOfferExtra';
import { dealTypeFromJsonQuery } from '../utils/category';
import { isSaleFromSuburbanBuilder } from '../utils/isSaleFromSuburbanBuilder';
import {
  getDealType,
  getObjectTypeWithJsonQuery,
  getHeadline,
  getOfferParentId,
  getObjectTypeWithCategory,
} from '../utils/trackings';
import { isExtended } from '../utils/offer/tracking';
import { mlSearchSession } from '../mlSearchSession';

interface IPageViewExtra {
  identicalOffers?: { id: number; offerType: 'offer' }[];
  rankingTags?: { name: string }[];
  extended_type?: string[];
  parentId?: number;
}

export const getCriteoProducts = (offers: IOffer[]) => offers.slice(0, 4).map(offer => Number(offer.id));

export const isPublished = (offer: IOffer) => offer.status === 'published';

export function getDealTypeForCustomUrl(type: string) {
  if (type.indexOf('rent') >= 0) {
    return 'rent';
  }

  if (type.indexOf('sale') >= 0) {
    return 'sale';
  }

  return '';
}

/* istanbul ignore next */
export function getOfferTypeorCustomUrl(type: string) {
  if (type.indexOf('flat') >= 0) {
    return 'flat';
  }

  if (type.indexOf('commercial') >= 0) {
    return 'commercial';
  }

  if (type.indexOf('suburban') >= 0) {
    return 'suburban';
  }

  return '';
}

/* istanbul ignore next */
export function getCustomUrl(jsonQuery: IJsonQuery) {
  const customUrl: Array<string | undefined | boolean | number> = [];

  const customUrlDealType = getDealTypeForCustomUrl((jsonQuery && jsonQuery._type) || '');
  const customUrlOfferType = getOfferTypeorCustomUrl((jsonQuery && jsonQuery._type) || '');

  if (customUrlDealType) {
    customUrl.push(customUrlDealType);
  }

  if (customUrlOfferType) {
    customUrl.push(customUrlOfferType);
  }

  return `/${customUrl.join('/')}/`;
}

/**
 * @returns объект данных для отправки в аналитику
 * @param searchGuid уникальный id поискового запроса
 * @param mlRankingGuid уникальный id ML-ранжирования
 * @param mlRankingModelVersion версия модели ML-ранжирования
 * @param mlSearchSessionGuid уникальный идентификатор поиска пользователя для ml-ранжирования.
 * Хранится на стороне браузера (sessionStorage). См. класс MlSearchSession.
 * @param searchRequestId - id запроса search-offers-desktop. На каждый запрос приходит новый id
 */
export const getGTMObjectData = (
  filters: IFiltersState,
  results: IResultsState,
  breadcrumbs: IBreadcrumb[] | null,
  user: TUser,
  userGALayerData: IUserGADataLayerData | null,
  profileSessionKey: string,
  fbRegion: string | undefined,
  fbCity: string | undefined,
  searchGuid: string | null,
  mlRankingGuid: string | null,
  mlRankingModelVersion: string | null,
  mlSearchSessionGuid: string | null,
  searchRequestId: string,
  extensions?: EOfferExtensionType[],
) => {
  const { jsonQueryUrl, jsonQuery: jsonQueryFromFilters } = filters;
  const { jsonQuery, offers, totalOffers, queryString } = results;

  const dealType = dealTypeFromJsonQuery(jsonQuery);
  const pageNumber = jsonQuery.page && jsonQuery.page.value ? Number(jsonQuery.page.value) : 1;

  const region =
    jsonQuery && jsonQuery.region
      ? String(jsonQuery.region.value[0])
      : jsonQueryFromFilters && jsonQueryFromFilters.region
      ? String(jsonQueryFromFilters.region.value[0])
      : '1';

  const hasExtensions = extensions && extensions.length;

  // позиции обычных и расширенных офферов считаются отдельно
  let extendedOfferPosition = 1;
  let regularOfferPosition = 1;

  const products = offers.map(offer => {
    const parentId = getOfferParentId(offer, jsonQuery);
    const { id, price, brand, variant, top3Place } = offerHelper(offer).getAnalyticsInfo();

    let extra: IPageViewExtra = {};
    const isCountrysideDeveloper = isSaleFromSuburbanBuilder(offer);
    const isOfferExtended = hasExtensions && isExtended(offer);
    const position = isOfferExtended ? extendedOfferPosition : regularOfferPosition;

    if (isOfferExtended) {
      const labelTypes = offer.extensionLabels ? offer.extensionLabels.map(({ labelType }) => labelType) : [];
      extra.extended_type = labelTypes;
      extendedOfferPosition += 1;
    } else {
      regularOfferPosition += 1;
    }

    if (offer.identicalOffers) {
      const { items } = offer.identicalOffers;
      // На выдаче показывается максимум 4 дубля для каждого объявления
      extra.identicalOffers = items.slice(0, 4).map(({ id }) => ({ id, offerType: 'offer' }));
    }

    if (parentId) {
      extra.parentId = parentId;
    }

    if (offer.photoLabels) {
      extra.rankingTags = offer.photoLabels.map(({ text }) => ({ name: text }));
    }

    const cplExtra = getOfferExtra(offer);
    if (cplExtra) {
      extra = {
        ...extra,
        ...cplExtra,
      };
    }

    return {
      brand,
      cianId: offer.cianId,
      dealType: getDealType(dealType),
      headline: getHeadline(offer),
      hiddenBase: Boolean(offer.isInHiddenBase),
      id,
      objectType: getObjectTypeWithCategory(offer.category, isCountrysideDeveloper),
      owner: Boolean(offer.isByHomeowner),
      ownerCianId: offer.cianUserId,
      ownerId: offer.userId,
      parentId: parentId || undefined,
      photosCount: offer.photos ? offer.photos.length : 0,
      podSnos: Boolean(offer.demolishedInMoscowProgramm),
      position,
      price,
      published: isPublished(offer),
      variant: variant ? variant.split('/') : [],
      ...(top3Place && { top3Place }),
      consultant: offer.isDealRequestSubstitutionPhone,
      extra,
    };
  });

  return {
    name: 'newpage',
    action: 'newpage',
    category: 'page',
    label: jsonQueryUrl || '',
    searchConditions: jsonQuery,

    user: {
      userId: user.isAuthenticated && user.userId ? user.userId : undefined,
      cianUserId: user.isAuthenticated && user.id ? user.id : undefined,
      email: user.isAuthenticated && user.email ? user.email : undefined,
      type: userGALayerData && userGALayerData.userType ? userGALayerData.userType : 'not_authorized',
      abGroup: (userGALayerData && Number(userGALayerData.abGroup)) || 0,
    },

    page: {
      breadCrumbs: breadcrumbs ? breadcrumbs.map(breadcrumb => breadcrumb.title) : [],
      pageNumber,
      pageType: 'Listing',
      region,
      objectType: getObjectTypeWithJsonQuery(jsonQuery),
      dealType: getDealType(dealType),
      siteType: 'desktop',
      offersQty: Number(totalOffers),
      sortType: (jsonQuery.sort && jsonQuery.sort.value) || 'default',
      listingType: hasExtensions ? 'extended' : 'list',
      queryString: queryString ? `/cat.php?${encodeURIComponent(queryString)}` : undefined,
      searchGuid,
      mlRankingGuid,
      mlRankingModelVersion,
      extra: {
        extended_type: hasExtensions ? extensions : undefined,
        mlSearchSessionGuid,
        searchRequestId,
      },
    },

    partner: {
      CriteoProducts: getCriteoProducts(offers),
      CriteoEmail: (userGALayerData && userGALayerData.criteoEmail) || undefined,
      CriteoNewCustomer: (userGALayerData && userGALayerData.criteoNewCustomer) || undefined,
      FB_City: fbRegion || undefined,
      FB_Region: fbCity || undefined,
    },

    products,
  };
};

/* istanbul ignore next */
export const trackPageView = (
  filters: IFiltersState,
  results: IResultsState,
  breadcrumbs: IBreadcrumb[] | null,
  user: TUser,
  userGALayerData: IUserGADataLayerData | null,
  profileSessionKey: string,
  fbRegion: string | undefined,
  fbCity: string | undefined,
  searchGuid: string | null,
  mlRankingGuid: string | null,
  mlRankingModelVersion: string | null,
  searchRequestId: string,
  extensions?: EOfferExtensionType[],
) => {
  const mlSearchSessionGuid = mlSearchSession.getSessionGuid(results.jsonQuery);

  return ca(
    'pageviewSite',
    getGTMObjectData(
      filters,
      results,
      breadcrumbs,
      user,
      userGALayerData,
      profileSessionKey,
      fbRegion,
      fbCity,
      searchGuid,
      mlRankingGuid,
      mlRankingModelVersion,
      mlSearchSessionGuid,
      searchRequestId,
      extensions,
    ),
  );
};
